import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { milestoneBurndown } from '@mkcore/reporting/schema';
import { Badge, Card, ErrorState, PageHeader, Skeleton } from '@mkcore/ui';
import { BurndownChart, CreatedClosedChart, HoursChart, VelocityChart } from '@mkcore/ui/charts';
import { call } from '@/lib/api';
import { formatDate, formatDuration } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';

export const Route = createFileRoute('/_app/dev/milestones_/$id')({ component: MilestoneCharts });

const verdictTone = { done: 'success', 'on-track': 'success', behind: 'danger', stalled: 'warning', 'no-due': 'outline' } as const;

/**
 * The charts of a milestone (CO-0308), computed on the server: burndown with ideal line and forecast, created versus
 * closed, velocity per week and the burndown in hours (estimate against time booked on its issues, CO-0275).
 */
function MilestoneCharts() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const q = useQuery({ queryKey: ['report', 'milestone', id], queryFn: () => call(milestoneBurndown, { milestoneId: id }) });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const r = q.data;
  return (
    <>
      <Link to="/dev/milestones" search={{ repositoryId: r.milestone.repositoryId }} className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('milestones.title')}
      </Link>
      <PageHeader
        eyebrow={r.milestone.repository}
        title={r.milestone.title}
        description={
          <span className="inline-flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={verdictTone[r.forecast.verdict]}>{t(`charts.verdict.${r.forecast.verdict}`)}</Badge>
            {t('charts.progress', { closed: r.scope - r.open, scope: r.scope })}
            {r.forecast.due && ` · ${t('charts.due', { date: formatDate(r.forecast.due, 'UTC') })}`}
            {r.forecast.finish && ` · ${t('charts.finish', { date: formatDate(r.forecast.finish, 'UTC') })}`}
            <Link to="/dev/issues" search={{ repositoryId: r.milestone.repositoryId, milestoneId: r.milestone.id, state: 'all' }} className="underline">
              {t('charts.issues')}
            </Link>
          </span>
        }
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <BurndownChart title={t('charts.burndown')} data={r.burndown} labels={{ remaining: t('charts.remaining'), ideal: t('charts.ideal') }} />
        </Card>
        <Card className="p-4">
          <HoursChart title={t('charts.hours')} data={r.hours.series} labels={{ spent: t('charts.spent'), remaining: t('charts.remainingHours') }} />
          <p className="mt-2 text-xs text-fg-3" data-testid="hours-summary">
            {t('charts.hoursSummary', { spent: formatDuration(r.hours.spentSeconds), estimate: formatDuration(r.hours.estimateSeconds) })}
            {r.hours.withoutEstimate > 0 && ` · ${t('charts.withoutEstimate', { count: r.hours.withoutEstimate })}`}
            {!r.hours.everyone && ` · ${t('charts.onlyMine')}`}
          </p>
        </Card>
        <Card className="p-4">
          <CreatedClosedChart title={t('charts.createdClosed')} data={r.createdClosed} labels={{ created: t('charts.created'), closed: t('charts.closed') }} />
        </Card>
        <Card className="p-4">
          <VelocityChart title={t('charts.velocity')} data={r.velocity} labels={{ closed: t('charts.closed'), created: t('charts.created') }} />
        </Card>
      </div>
    </>
  );
}
